import { Accordion } from "@sede-x/shell-ds-react-framework";
import { useEffect, useRef } from "react";
import { TBatchSearchCriteria } from "ticketing/ticketing.types";
import { SearchMovementsOrTicketsForm } from "./SearchMovementsOrTicketsForm";

type TSearchMovementsOrTicketsContainerProps = {
  searchCriteria?: TBatchSearchCriteria;
  onSearch: (criteria: TBatchSearchCriteria) => void;
  searchFor: "Movements" | "Tickets";
  open: boolean;
  onCollapsed: (collpased: boolean) => void;
  onReset: () => void;
};

export default function SearchMovementsOrTicketsContainer({
  searchCriteria,
  onSearch,
  searchFor,
  open,
  onCollapsed,
  onReset
}: Readonly<TSearchMovementsOrTicketsContainerProps>) {
  const accordionRef = useRef<HTMLDivElement>(null);
  const prevHeightRef = useRef<number>(0);
  const SIGNIFICANT_HEIGHT_CHANGE_THRESHOLD = 20;

  // Setup ResizeObserver to detect height changes (indicates expand/collapse)
  useEffect(() => {
    if (!accordionRef.current) {
      return () => {};
    }

    const accordionEl = accordionRef.current;
    const resizeObserver = new ResizeObserver(entries => {
      for (const entry of entries) {
        const currentHeight = entry.contentRect.height;

        // If height changed significantly, the accordion likely expanded/collapsed
        if (
          Math.abs(currentHeight - prevHeightRef.current) > SIGNIFICANT_HEIGHT_CHANGE_THRESHOLD
        ) {
          const isExpanded = currentHeight > prevHeightRef.current;

          if ((isExpanded && !open) || (!isExpanded && open)) {
            onCollapsed(isExpanded);
          }
          prevHeightRef.current = currentHeight;
        }
      }
    });
    // Start observing the accordion element
    resizeObserver.observe(accordionEl);
    // Clean up
    return () => {
      resizeObserver.unobserve(accordionEl);
      resizeObserver.disconnect();
    };
  }, [accordionRef, onCollapsed, open]);

  // Set initial defaultActiveKey based on open prop
  const defaultActiveKey = open ? "1" : undefined;

  return (
    <div ref={accordionRef}>
      <Accordion
        defaultActiveKey={defaultActiveKey}
        iconSet="chevron"
        items={[
          {
            key: 1,
            label: `Search ${searchFor}`,
            children: (
              <div className="form-container">
                <SearchMovementsOrTicketsForm
                  searchCriteria={searchCriteria}
                  onSearch={onSearch}
                  searchFor={searchFor}
                  onReset={onReset}
                />
              </div>
            )
          }
        ]}
        bodyPadding={false}
        fullWidth={true}
      />
    </div>
  );
}
